<template>
    <div class="print:hidden mx-1">
        <transition-group
        tag="div"
        enter-active-class="duration-400 ease-out"
        enter-from-class="transform opacity-0 -translate-y-10"
        enter-to-class="opacity-100"
        leave-active-class="duration-400 ease-out absolute"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0 -translate-y-10"
        move="transition-all duration-400 ease"
        >
            <div class="transition-all duration-300 ease-out" key="company-info">
                <div class="pt-1 flex flex-wrap items-end gap-1 mx-1.5">
                  <div class="w-56 max-w-full">
                    <base-filter @click="setClientOptions" :active="invoiceFilters.client.value.length > 0">
                        <template v-slot:label> Client </template>
                        <template v-slot:widget>
                        <BaseComboBox
                            class="w-full rounded-b-md text-black"
                            v-model="invoiceFilters.client.value"
                            :options="invoiceFilters.client.options"
                            @update:modelValue="setClientFilter"
                            :multiple="true"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="w-64 max-w-full">
                    <base-filter @click="setEngagementOptions" :active="invoiceFilters.engagement.value.length > 0">
                        <template v-slot:label> Engagement </template>
                        <template v-slot:widget>
                        <BaseComboBox
                            class="w-full rounded-b-md text-black"
                            v-model="invoiceFilters.engagement.value"
                            :options="invoiceFilters.engagement.options"
                            @update:modelValue="setEngagementFilter"
                            :multiple="true"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="w-40 max-w-full">
                    <base-filter :active="!!invoiceFilters.invoiceNumber.value">
                        <template v-slot:label> Invoice # </template>
                        <template v-slot:widget>
                        <input
                            type="number"
                            min="0"
                            v-model="invoiceFilters.invoiceNumber.value"
                            class="w-full rounded-b-md text-black border border-gray-300 px-2 py-1 text-sm"
                            placeholder="e.g. 1234"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="w-44 max-w-full">
                    <base-filter :active="!!invoiceFilters.status.value">
                        <template v-slot:label> Status </template>
                        <template v-slot:widget>
                        <base-list-box
                            class="w-full rounded-b-md text-black"
                            v-model="invoiceFilters.status.value"
                            :options="invoiceFilters.status.options"
                            :multiple="false"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="w-44 max-w-full">
                    <base-filter :active="invoiceFilters.dueDate.value.length > 0">
                        <template v-slot:label> Due Date </template>
                        <template v-slot:widget>
                        <base-list-box
                            class="w-full rounded-b-md text-black"
                            v-model="invoiceFilters.dueDate.value"
                            :options="invoiceFilters.dueDate.options"
                            @update:modelValue="setDueDateFilter"
                            :multiple="true"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="w-40 max-w-full">
                    <base-filter :active="!!invoiceFilters.isSent.value">
                        <template v-slot:label> Is Sent </template>
                        <template v-slot:widget>
                        <base-list-box
                            class="w-full rounded-b-md text-black"
                            v-model="invoiceFilters.isSent.value"
                            :options="invoiceFilters.isSent.options"
                            :multiple="false"
                        />
                        </template>
                    </base-filter>
                  </div>
                  <div class="flex flex-wrap items-center gap-1 ml-auto self-center">
                    <base-button-affirmative @click="resetFilters" class="text-xs -py-2">Reset</base-button-affirmative>
                    <base-button @click="setShowAll">Show All</base-button>
                    <base-button @click="setDueSoonest">Due Soonest</base-button>
                    <base-button @click="setDraft">Draft</base-button>
                    <base-button @click="setUnsent">Unsent</base-button>
                    <base-button @click="setPaid">Paid</base-button>
                  </div>
                </div>
            </div>
        </transition-group>
        <div>
        <div class="flex flex-wrap items-center gap-2 mx-1.5">
          <h3 class="text-sm">Active Filters:</h3>
          <base-pill class="bg-amber-300 text-xs flex items-center justify-between space-x-2 p-2 mb-0.5 " v-for="filter in activeFilters" :key="filter.name">
            <div>{{ filter.name }}: {{ filter.value }}</div><button @click="clearFilter(filter.filter)">✕</button>
          </base-pill>
          <span v-if="orderBy && orderBy.length" class="text-sm pl-2 border-l border-gray-300">Active Sorting:</span>
          <base-pill v-if="orderBy && orderBy.length" class="bg-amber-300 text-xs flex items-center justify-between space-x-2 p-2 mb-0.5">
            <div>{{ orderBy }}</div><button @click="emit('clear-sort')">✕</button>
          </base-pill>
        </div>
      </div>
    </div>
</template>

<script>
export default {
    name: 'invoice-filters'
}
</script>

<script setup>
import { ref, watch, reactive, defineEmits, defineProps, computed } from 'vue';
import {useStore} from 'vuex'
import { getAllClientsSimple } from '@/services/Client';
import { getEngagementsSimple } from '@/services/Engagement';
import BaseComboBox from '../BaseComboBox.vue'

const store = useStore()

const props = defineProps({
  orderBy: { type: Array, default: () => [] },
});

const emit = defineEmits([
  "change-invoice-filters",
  "change-ordering",
  "clear-sort",
]);

// Reference props so the linter doesn't complain when only the template uses them
void props;

const invoiceFilters = reactive({
    client: {
        name: "Client",
        value: [],
        defaultValue: [],
        options: [
      {value: "clear", label: "------------"}
    ]
    },
    status: {
        name: "Status",
        value: "unpaid",
        defaultValue: "unpaid",
        options: [
      {value: null, label: "------------"},
      {value: "draft", label: "draft"},
      {value: "paid", label: "paid"},
      {value: "unpaid", label: "unpaid"},
      {value: "write_off", label: "write off"},
      {value: "paused", label: "paused"},
    ]
    },
    dueDate: {
        name: "Due Date",
        value: [],
        defaultValue: [],
        options: [
      {value: "clear", label: "------------"},
      {value: "overdue", label: "Overdue"},
      {value: "three_days", label: "Three Days"},
      {value: "five_days", label: "Five Days"},
      {value: "ten_days", label: "Ten Days"},
    ]
    },
    isSent: {
        name: "Is Sent",
        value: "True",
        defaultValue: "True",
        options: [
            {value: null, label: "------------"},
            {value: "True", label: "True"},
            {value: "False", label: "False"},
        ]
    },
    engagement: {
        name: "Engagement",
        value: [],
        defaultValue: [],
        options: [
            {value: "clear", label: "------------"},
        ]
    },
    invoiceNumber: {
        name: "Invoice #",
        value: null,
        defaultValue: null,
        options: []
    }
})

const filterValues = computed(() => {
    let values = []
    for(const filter in invoiceFilters){
        values.push(invoiceFilters[filter].value)
    }
    return values
})

const clientOptions = ref([
      {value: "clear", label: "------------"}
    ])
const statusOptions = ref([
      {value: null, label: "------------"},
      {value: "paid", label: "paid"},
      {value: "unpaid", label: "unpaid"},
    ])
const dueDateOptions = ref([
      {value: "clear", label: "------------"},
      {value: "overdue", label: "Overdue"},
      {value: "three_days", label: "Three Days"},
      {value: "five_days", label: "Five Days"},
      {value: "ten_days", label: "Ten Days"},
    ])

async function setClientOptions() {
      const token = store.state.auth.token
      const result = await getAllClientsSimple({token: token})
    //   clientOptions.value = [{value: "clear", label: "------------"}, ...result.clients.map((client) => {
    //     return {value: client.id, label: client.client_name}
    //   })]
      invoiceFilters.client.options = [{value: "clear", label: "------------"}, ...result.data.map((client) => {
        return {value: client.id, label: client.client_name}
      })]
    }

let engagementsLoaded = false
async function setEngagementOptions() {
    if (engagementsLoaded) return
    const token = store.state.auth.token
    const rows = await getEngagementsSimple(token)
    invoiceFilters.engagement.options = [
        {value: "clear", label: "------------"},
        ...rows.map((r) => ({value: r.id, label: r.label})),
    ]
    engagementsLoaded = true
}

function setClientFilter(target) {
      if(target.includes("clear")){
        invoiceFilters.client.value = []
      }
    }
function setDueDateFilter(target) {
      if(target.includes("clear")){
        invoiceFilters.dueDate.value = []
      }
    }
function setEngagementFilter(target) {
      if(target.includes("clear")){
        invoiceFilters.engagement.value = []
      }
    }

watch(filterValues, () => {
    emitFiltersChanged()
}, {deep: true})

// When the user starts typing an invoice number they're clearly hunting for
// one specific invoice, so clear every other active filter (including the
// default status=unpaid / isSent=True) that would otherwise hide it. Only fire
// on the empty -> non-empty transition so it doesn't fight the user mid-search.
watch(() => invoiceFilters.invoiceNumber.value, (newVal, oldVal) => {
    if (!newVal || oldVal) return
    for (const filter in invoiceFilters) {
        if (filter === 'invoiceNumber') continue
        invoiceFilters[filter].value = Array.isArray(invoiceFilters[filter].value) ? [] : null
    }
})

function emitFiltersChanged(){
    emit("change-invoice-filters", invoiceFilters)
}

function resetFilters() {
    for(const filter in invoiceFilters){
        invoiceFilters[filter].value = invoiceFilters[filter].defaultValue
    }
    emit('change-ordering', ['-due_date'])
}

function clearAllFilters() {
    for(const filter in invoiceFilters){
        if(Array.isArray(invoiceFilters[filter].value)){
            invoiceFilters[filter].value = []
        } else {
            invoiceFilters[filter].value = null
        }
    }
}

function setShowAll() {
    clearAllFilters()
    emit('change-ordering', ['-due_date'])
}

function setDraft() {
    clearAllFilters()
    invoiceFilters.status.value = 'draft'
}

function setUnsent() {
    clearAllFilters()
    invoiceFilters.isSent.value = 'False'
    invoiceFilters.status.value = 'unpaid'
}

function setPaid() {
    clearAllFilters()
    invoiceFilters.status.value = 'paid'
    emit('change-ordering', ['-date_paid'])
}

function clearFilter(filterName) {
    if(Array.isArray(invoiceFilters[filterName].value)){
        invoiceFilters[filterName].value = []
    } else {
        invoiceFilters[filterName].value = null
    }
}

const activeFilters = computed(() => {
    let activeFilterArr = []
    for(const filter in invoiceFilters) {
        if(Array.isArray(invoiceFilters[filter].value) && invoiceFilters[filter].value.length > 0){
            console.log('found array')
            activeFilterArr.push({filter: filter, name: invoiceFilters[filter].name, value: invoiceFilters[filter].options.filter(option => invoiceFilters[filter].value.includes(option.value)).map(option => option.label)})
        } else if (!Array.isArray(invoiceFilters[filter].value) && invoiceFilters[filter].value) {
            activeFilterArr.push({filter: filter, name: invoiceFilters[filter].name, value: invoiceFilters[filter].value})
        }
    }
    return activeFilterArr
})

setClientOptions()

function setDueSoonest() {
    invoiceFilters.status.value = 'unpaid'
    emit('change-ordering', ['due_date'])

}

</script>